import { t } from '../lib/i18n';
import { useState } from 'react';

/**
 * Charts for the admin dashboard, drawn as plain SVG.
 *
 * No chart library: the bundle already carries a map engine, and three small
 * components that follow the site's own colour tokens — dark mode included —
 * cost less than one dependency that would have to be themed to match.
 *
 * Every chart scales with its container through the viewBox, so the same
 * component works on a phone and a wide screen without measuring anything.
 */

export type Point = { date: string; value: number };
export type Slice = { label: string; value: number };

const W = 640;
const H = 220;
const PAD = { top: 16, right: 12, bottom: 26, left: 40 };

/** A round, readable axis maximum: 7 → 8, 43 → 50, 1 240 → 1 500. */
function niceMax(value: number): number {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const step = steps.find((candidate) => candidate * magnitude >= value) ?? 10;
  return step * magnitude;
}

const shortDate = (iso: string): string => {
  const [, month, day] = iso.split('-');
  return `${Number(day)}/${Number(month)}`;
};

/**
 * One or more daily series on shared axes. Hovering (or tapping) shows the
 * exact numbers for that day.
 */
export function LineChart({
  series,
  format = (value) => String(value),
}: {
  series: { label: string; color: string; points: Point[] }[];
  format?: (value: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const days = series[0]?.points ?? [];
  const n = days.length;
  const max = niceMax(Math.max(1, ...series.flatMap((line) => line.points.map((point) => point.value))));

  const x = (index: number) => PAD.left + (n <= 1 ? 0 : (index / (n - 1)) * (W - PAD.left - PAD.right));
  const y = (value: number) => PAD.top + (1 - value / max) * (H - PAD.top - PAD.bottom);

  // About five date labels, however long the window.
  const labelEvery = Math.max(1, Math.ceil(n / 5));

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={series.map((line) => line.label).join(', ')}
        onMouseLeave={() => setHover(null)}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
          <g key={ratio}>
            <line className="chart__grid" x1={PAD.left} x2={W - PAD.right} y1={y(max * ratio)} y2={y(max * ratio)} />
            <text className="chart__axis" x={PAD.left - 6} y={y(max * ratio) + 4} textAnchor="end">
              {format(Math.round(max * ratio))}
            </text>
          </g>
        ))}

        {days.map((point, index) =>
          index % labelEvery === 0 || index === n - 1 ? (
            <text key={point.date} className="chart__axis" x={x(index)} y={H - 8} textAnchor="middle">
              {shortDate(point.date)}
            </text>
          ) : null,
        )}

        {series.map((line) => {
          const path = line.points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(point.value)}`).join(' ');
          const area = `${path} L${x(n - 1)},${y(0)} L${x(0)},${y(0)} Z`;
          return (
            <g key={line.label}>
              <path d={area} fill={line.color} opacity={0.1} />
              <path d={path} fill="none" stroke={line.color} strokeWidth={2.2} strokeLinejoin="round" />
            </g>
          );
        })}

        {hover !== null ? (
          <line className="chart__cursor" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} />
        ) : null}

        {/* Invisible columns that pick the day under the pointer. */}
        {days.map((point, index) => (
          <rect
            key={point.date}
            x={x(index) - (W - PAD.left - PAD.right) / Math.max(1, n - 1) / 2}
            y={PAD.top}
            width={(W - PAD.left - PAD.right) / Math.max(1, n - 1)}
            height={H - PAD.top - PAD.bottom}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
            onTouchStart={() => setHover(index)}
          />
        ))}
      </svg>

      <div className="chart__legend">
        {hover !== null && days[hover] ? <strong>{shortDate(days[hover].date)}</strong> : null}
        {series.map((line) => (
          <span key={line.label}>
            <i style={{ background: line.color }} />
            {line.label}
            {hover !== null ? ` : ${format(line.points[hover]?.value ?? 0)}` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Daily bars, for amounts where each day stands alone (fees collected). */
export function BarChart({
  points,
  color,
  format = (value) => String(value),
}: {
  points: Point[];
  color: string;
  format?: (value: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const n = points.length;
  const max = niceMax(Math.max(1, ...points.map((point) => point.value)));
  const inner = W - PAD.left - PAD.right;
  const slot = inner / Math.max(1, n);
  const y = (value: number) => PAD.top + (1 - value / max) * (H - PAD.top - PAD.bottom);
  const labelEvery = Math.max(1, Math.ceil(n / 5));

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" onMouseLeave={() => setHover(null)}>
        {[0, 0.5, 1].map((ratio) => (
          <g key={ratio}>
            <line className="chart__grid" x1={PAD.left} x2={W - PAD.right} y1={y(max * ratio)} y2={y(max * ratio)} />
            <text className="chart__axis" x={PAD.left - 6} y={y(max * ratio) + 4} textAnchor="end">
              {format(Math.round(max * ratio))}
            </text>
          </g>
        ))}
        {points.map((point, index) => (
          <g key={point.date}>
            <rect
              x={PAD.left + index * slot + slot * 0.15}
              y={y(point.value)}
              width={slot * 0.7}
              height={Math.max(0, y(0) - y(point.value))}
              rx={Math.min(3, slot * 0.2)}
              fill={color}
              opacity={hover === null || hover === index ? 1 : 0.45}
            />
            <rect
              x={PAD.left + index * slot}
              y={PAD.top}
              width={slot}
              height={H - PAD.top - PAD.bottom}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
              onTouchStart={() => setHover(index)}
            />
            {index % labelEvery === 0 || index === n - 1 ? (
              <text className="chart__axis" x={PAD.left + index * slot + slot / 2} y={H - 8} textAnchor="middle">
                {shortDate(point.date)}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      <div className="chart__legend">
        {hover !== null && points[hover] ? (
          <span>
            <strong>{shortDate(points[hover].date)}</strong> : {format(points[hover].value)}
          </span>
        ) : (
          <span className="muted">{t("Survolez une barre pour le détail")}</span>
        )}
      </div>
    </div>
  );
}

/** A ranked breakdown: one labelled bar per row, widest first. */
export function HBars({
  slices,
  color,
  labels = {},
}: {
  slices: Slice[];
  color: string;
  /** Display names for codes (COLOCATION → Colocation). */
  labels?: Record<string, string>;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const max = Math.max(1, ...slices.map((slice) => slice.value));
  if (slices.length === 0 || total === 0) {
    return <p className="small muted" style={{ margin: 0 }}>{t("Pas encore de données.")}</p>;
  }
  return (
    <div className="hbars">
      {[...slices]
        .sort((a, b) => b.value - a.value)
        .map((slice) => (
          <div key={slice.label} className="hbars__row">
            <span className="hbars__label">{labels[slice.label] ?? t(slice.label)}</span>
            <span className="hbars__track">
              <span className="hbars__fill" style={{ width: `${(slice.value / max) * 100}%`, background: color }} />
            </span>
            <span className="hbars__value">
              {slice.value}
              <span className="muted"> · {Math.round((slice.value / total) * 100)} %</span>
            </span>
          </div>
        ))}
    </div>
  );
}
